import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Divider,
  Avatar,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import { Person as PersonIcon, ArrowForward, CheckCircle } from '@mui/icons-material';
import config from '../config';

const ReferralLanding = () => {
  const [referralCode, setReferralCode] = useState('');
  const [referrer, setReferrer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Extract referral code from URL
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('ref');
    
    if (!code) {
      setError('No referral code provided');
      setLoading(false);
      return;
    }
    
    setReferralCode(code);
    
    // Fetch referrer information
    fetchReferrerInfo(code);
  }, [location]);
  
  const fetchReferrerInfo = async (code) => {
    try {
      const response = await fetch(`${config.API_URL}/api/referral/referrer-info?code=${code}`);
      
      if (!response.ok) {
        throw new Error('Invalid referral code or referrer not found');
      }
      
      const data = await response.json();
      setReferrer(data.referrer);
    } catch (error) {
      console.error('Error fetching referrer info:', error);
      setError(error.message || 'Failed to fetch referrer information');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRegister = () => {
    navigate(`/register?ref=${referralCode}`);
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
              <Avatar sx={{ width: 80, height: 80, mb: 2, bgcolor: 'primary.main' }}>
                <PersonIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h4" align="center" gutterBottom>
                You've Been Invited!
              </Typography>
              <Typography variant="h6" align="center" color="text.secondary" gutterBottom>
                {referrer ? `${referrer.email} has invited you to join our platform` : 'You have been invited to join our platform'}
              </Typography>
            </Box>
            
            <Divider sx={{ mb: 4 }} />
            
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                Join Our Platform and Enjoy These Benefits:
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                    <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" align="center" gutterBottom>
                      Special Bonus
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary">
                      Get started with exclusive benefits as a referred member
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                    <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" align="center" gutterBottom>
                      Full Access
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary">
                      Get complete access to all our premium features
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                    <CheckCircle color="success" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h6" align="center" gutterBottom>
                      Referral Program
                    </Typography>
                    <Typography variant="body2" align="center" color="text.secondary">
                      Refer others and earn rewards yourself
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Button 
                variant="contained" 
                size="large" 
                endIcon={<ArrowForward />}
                onClick={handleRegister}
                sx={{ minWidth: 200, py: 1.5 }}
              >
                Register Now
              </Button>
              
              <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                Already have an account? <Link to="/login" style={{ color: 'inherit', fontWeight: 'bold' }}>Log in here</Link>
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ReferralLanding; 